import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

/**
 * Modal for assigning an accessible organization to a Space Lite record.
 */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.input.textArea.Component,
    ds.input.select.single.Component,
    ds.input.dateRange.multiple.Component,
    ds.input.checkbox.Component,
    ds.input.radio.option.Component,
    ds.input.radio.options.Component,
    ds.input.radio.value.Directive,
  ],
  selector: 'atn-space-organization-assignment-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  // TODO: Make an input for recordAssignedOrgsEc to allow reuse between
  // full and lite spaces
  /** @see {@link state.space.recordAssignedOrgs.Collection} */
  public readonly recordAssignedOrgsEc = ngCore.inject(
    state.space.lite.recordAssignedOrgs.Collection,
  )

  /** Keep user selections per entity */
  public readonly selectionLookupSg = ngCore.signal<
    Partial<ents.extra.space.Organization.Lookup>
  >({})

  /** Whether the leaf organization has been selected — enables the Save button */
  public readonly leafOrganizationSelectedSg = ngCore.computed(
    (
      selectionLookup = this.selectionLookupSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      const leafEntity = schema.tryGetLeafEntityOf(
        state.space.organizations.constants.DIMENSION_ID,
      )
      return (
        TypeChecker.isNotNullish(leafEntity) &&
        TypeChecker.isNotNullish(selectionLookup[leafEntity.id])
      )
    },
  )

  /** Editing assignment */
  private readonly _editedAssignmentSg = ngCore.computed(() =>
    this.recordAssignedOrgsEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal(),
  )

  /** Is editing assignment an occupier */
  public readonly isOccupierSg = ngCore.computed(
    (assignment = this._editedAssignmentSg()) => assignment?.isOccupier,
  )

  /** Description of the editing assignment */
  public readonly descriptionSg = ngCore.computed(
    (assignment = this._editedAssignmentSg()) => assignment?.description,
  )

  /**
   * Computes available organization entities and their selectable record options
   * based on current selection.
   */
  public readonly organizationEntityOptionsSg = ngCore.computed(
    (
      selectionLookup = this.selectionLookupSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) =>
      schema
        .getEntitiesInDimension(
          state.space.organizations.constants.DIMENSION_ID,
        )
        .map(entity => {
          const parentEntity = schema.tryGetParentEntityOf(entity)
          const parentId = parentEntity
            ? selectionLookup[parentEntity.id]
            : undefined
          const orgEc = this._organizationsFeature.selectors
            .getOrganizationEc(entity.id)
            .asSignal()

          return {
            id: entity.id,
            name: entity.name,
            options:
              orgEc?.plugins.cachePlugin.feature.selectors
                .getCachedEntities()
                .asSignal()
                .filter(TypeChecker.isNotNullish.raw)
                .filter(o => o.parentId === parentId)
                .map(({ name: label, id: value }) => ({ label, value })) ?? [],
          }
        }),
  )

  constructor() {
    /** Sets default isOccupier when missing */
    ngCore.effect(() => {
      const assignment = this._editedAssignmentSg()
      if (assignment && TypeChecker.isNullish(assignment.isOccupier)) {
        ngCore.untracked(() => this.updateOrgAssignment({ isOccupier: true }))
      }
    })
  }

  /**
   * Handles selection of an organization record at a given entity.
   */
  public handleOrganizationSelection(
    entityId: ents.core.dms.models.Entity.Id,
    organizationId?: ents.extra.space.Organization.Id,
  ): void {
    const schema = this._schemaFeature.selectors.schema.asSignal()

    if (schema.isLeaf(entityId) && TypeChecker.isNotNullish(organizationId)) {
      this.updateOrgAssignment({ organizationId })
    }

    const ancestorIds = new Set(schema.getAncestorsOf(entityId).map(e => e.id))

    this.selectionLookupSg.update(prev => {
      // Keep only ancestor selections, clearing the current entity and descendants.
      const pruned: Partial<ents.extra.space.Organization.Lookup> = {}
      for (const ancestorId of ancestorIds) {
        const orgId = prev[ancestorId]
        if (TypeChecker.isNotNullish(orgId)) {
          pruned[ancestorId] = orgId
        }
      }
      if (TypeChecker.isNotNullish(organizationId)) {
        pruned[entityId] = organizationId
      }
      return pruned
    })
  }

  /**
   * Updates the currently edited assignment.
   */
  public updateOrgAssignment(
    input: Partial<ents.extra.space.Record.OrganizationAssignment.Input>,
  ): void {
    this.recordAssignedOrgsEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.OrgAssignment.Modal.Inputted')
      .dispatch({
        edit: { input },
      })
  }

  /** Submit changes handler */
  public saveClickHandler = () => {
    this.recordAssignedOrgsEc.plugins.editingPlugin.feature.actions.submit
      .from('OrgAssignment.Modal.SaveButton.Clicked')
      .dispatch({ current: true })
  }

  /** Discard changes handler */
  public cancelClickHandler = () => {
    this.recordAssignedOrgsEc.plugins.editingPlugin.feature.actions.discard
      .from('OrgAssignment.Modal.CancelButton.Clicked')
      .dispatch({ current: true })
  }
}
